import { useState, useEffect } from "react";
import { colors, fonts, frames, mediaQueriesNumbers, useMediaQuery } from "../../../../../Styles";
import { AicoConnection, PageHeader, AreaLayout, TableTypes, LoadingSpinner, NewInput } from "../../../../../Components";
import { LocaleFarmDate, NewFieldStyles } from "../../../../../Definitions";
import { useLazyQuery } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import { Connected1801DevicesArgs, Connected1801DevicesResponse, CONNECTED_1801_DEVICES, VP1801Info } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/connected1801Devices";
import React from "react";

type TrackedDevice = VP1801Info & {
	isConnected: boolean;
};

export default function Connected1801List() {
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);

	const [searchById, setSearchById] = useState("");
	const [searchByIp, setSearchByIp] = useState("");

	const [devicesMap, setDevicesMap] = useState<Map<string, TrackedDevice>>(new Map());

	const { state: connectionState } = useAicoConnection();

	const [
		fetchDevices,
		{
			data: dataDevices,
			loading: loadingDevices,
			error: errorDevices
		}
	] = useLazyQuery<Connected1801DevicesResponse, Connected1801DevicesArgs>(
		CONNECTED_1801_DEVICES,
		{ fetchPolicy: "network-only" },
	);

	useEffect(() => {
		setDevicesMap(new Map());

		if (!connectionState.domain) return;

		fetchDevices({ variables: { domain: connectionState.domain } });

		const interval = setInterval(() => {
			if (!connectionState.domain) return;
			fetchDevices({ variables: { domain: connectionState.domain } });
		}, 5000);

		return () => clearInterval(interval);
	}, [connectionState.domain, fetchDevices]);

	useEffect(() => {
		const incoming = dataDevices ? dataDevices.device.connected1801Devices : [];
		const incomingIds = new Set(incoming.map((d) => d.id));

		setDevicesMap((prev) => {
			const next = new Map(prev);

			// 1. Mark all existing as disconnected initially
			Array.from(next.entries()).forEach(([id, device]) => {
				next.set(id, {
					...device,
					isConnected: incomingIds.has(id),
				});
			});

			// 2. Upsert incoming devices as connected
			for (const device of incoming) {
				next.set(device.id, {
					...next.get(device.id),
					...device,
					isConnected: true,
				});
			}

			return next;
		});
	}, [dataDevices]);

	return (
		<frames.Page_inner>
			<PageHeader topHeader='Server' subHeader='Connected 1801 list' />
			<frames._page_inner_content>
				<AreaLayout
					tableType={TableTypes.fullPage}
					headerArea={mobileXs ? undefined : <AicoConnection hideLANControllers inTable />}
					buttonArea={
						<div style={{ display: "flex", gap: "5px" }}>
							{mobileXs && <AicoConnection hideLANControllers inTable />}
						</div>
					}
					topArea={
						<div
							style={{
								display: "flex",
								alignItems: "flex-start",
								justifyContent: "space-between",
								marginBottom: "12px",
								gap: "12px",
							}}
						>
							<div
								style={{
									flex: 1,
									maxWidth: "320px",
									display: "flex",
									flexDirection: "column",
									gap: "6px",
								}}
							>
								<NewInput
									placeholder={"search by id"}
									onChange={(e) => setSearchById(e)}
									fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
									isClearable
								/>

								<NewInput
									placeholder={"search by ip"}
									onChange={(e) => setSearchByIp(e)}
									fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
									isClearable
								/>
							</div>

							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-end",
									gap: "10px",
									minHeight: "50px",
								}}
							>
								{loadingDevices && (
									<div style={{ display: "flex", alignItems: "center" }}>
										<LoadingSpinner />
									</div>
								)}

								{errorDevices && (
									<div
										style={{
											...fonts.paragraph,
											...fonts.fontSize4,
											...fonts.fontWeight2,
											color: colors.systemRed,
										}}
									>
										Error loading devices
									</div>
								)}
							</div>
						</div>
					}
					contentArea={
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
								gap: "20px",
								width: "100%",
							}}
						>
							{Array.from(devicesMap.values())
								.filter((e) =>
									e.id.toLowerCase().includes(searchById.toLowerCase())
									&& e.ipAddress.toLowerCase().includes(searchByIp.toLowerCase())
								)
								.map((e) => (
									<div
										key={e.id}
										style={{
											border: `1px solid ${colors.greyLightSecondary}`,
											borderRadius: "12px",
											padding: "14px",
											display: "flex",
											flexDirection: "column",
											justifyContent: "space-between",
											height: "320px",
											background: colors.whitePrimary,
											boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												marginBottom: "8px",
											}}
										>
											<div>
												<div
													style={{
														...fonts.paragraph,
														...fonts.fontSize5,
														color: colors.greyPrimary,
													}}
												>
													ID
												</div>
												<div
													style={{
														...fonts.paragraph,
														...fonts.fontSize3,
														...fonts.fontWeight2,
														color: colors.blackPrimary,
													}}
												>
													{e.id}
												</div>
											</div>

											<div
												style={{
													width: "10px",
													height: "10px",
													borderRadius: "50%",
													background: e.isConnected
														? colors.systemGreen
														: colors.systemRed,
												}}
											/>
										</div>

										<div
											style={{
												display: "grid",
												gridTemplateColumns: "1fr 1fr",
												gap: "8px",
												marginBottom: "8px",
											}}
										>
											<div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize5, color: colors.greyPrimary }}>
													IP Address
												</div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize3, color: colors.blackPrimary }}>
													{e.ipAddress}:{e.port}
												</div>
											</div>

											<div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize5, color: colors.greyPrimary }}>
													MAC Address
												</div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize3, color: colors.blackPrimary }}>
													{e.macAddress || "-"}
												</div>
											</div>
										</div>

										<div
											style={{
												display: "grid",
												gridTemplateColumns: "1fr 1fr",
												gap: "8px",
												marginBottom: "8px",
											}}
										>
											<div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize5, color: colors.greyPrimary }}>
													Last Heartbeat
												</div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.blackPrimary }}>
													{new LocaleFarmDate(
														e.lastHeartbeat * 1000
													).toLocaleFarmString()}
												</div>
											</div>

											<div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize5, color: colors.greyPrimary }}>
													First Connection
												</div>
												<div style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.blackPrimary }}>
													{new LocaleFarmDate(
														e.firstConnectionAt * 1000
													).toLocaleFarmString()}
												</div>
											</div>
										</div>

										<div
											style={{
												display: "grid",
												gridTemplateColumns: "1fr 1fr",
												gap: "10px",
												flexGrow: 1,
											}}
										>
											{[
												{ label: "Antenna 1", data: e.antenna1TagInfo },
												{ label: "Antenna 2", data: e.antenna2TagInfo },
											].map((ant, i) => {
												const d = ant.data;

												return (
													<div
														key={i}
														style={{
															border: `1px solid ${colors.greyLightTertiary}`,
															borderRadius: "8px",
															padding: "8px",
															display: "flex",
															flexDirection: "column",
															gap: "8px",
															background: colors.whitePrimary,
														}}
													>
														<div
															style={{
																...fonts.header,
																...fonts.fontSize5,
																color: colors.greyDarkPrimary,
															}}
														>
															{ant.label} Tag Info
														</div>

														<div>
															<div style={{ ...fonts.paragraph, ...fonts.fontSize5, color: colors.greyPrimary }}>
																Tag
															</div>
															<div
																style={{
																	...fonts.paragraph,
																	...fonts.fontSize3,
																	...fonts.fontWeight2,
																	color: colors.blackPrimary,
																}}
															>
																{d?.tag || "-"}
															</div>
														</div>

														<div>
															<div style={{ ...fonts.paragraph, ...fonts.fontSize5, color: colors.greyPrimary }}>
																Seen At
															</div>
															<div style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.blackPrimary }}>
																{d
																	? new LocaleFarmDate(
																		d.seenAt * 1000
																	).toLocaleFarmString()
																	: "-"}
															</div>
														</div>

														<div
															style={{
																display: "grid",
																gridTemplateColumns: "auto 1fr auto 1fr",
																gap: "4px 10px",
																alignItems: "center",
															}}
														>
															{[
																["Data Bit", d?.dataBit],
																["Animal Bit", d?.animalBit],
																["Type", d?.type],
																["Strength", d?.strength],
																["Retag", d?.retaggingCounter],
																["User", d?.userInfo],
																["Reserved", d?.reservedField],
															].map(([label, value], i) => (
																<React.Fragment key={i}>
																	<div
																		style={{
																			...fonts.paragraph,
																			...fonts.fontSize6,
																			color: colors.greySecondary,
																		}}
																	>
																		{label}
																	</div>
																	<div
																		style={{
																			...fonts.paragraph,
																			...fonts.fontSize6,
																			color: colors.blackPrimary,
																		}}
																	>
																		{value ?? "-"}
																	</div>
																</React.Fragment>
															))}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								))}
						</div>
					}
				/>
			</frames._page_inner_content>
		</frames.Page_inner>
	);
}